import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Plus, Trash, UploadSimple, DownloadSimple, ArrowsClockwise, PencilSimple, ArrowsLeftRight } from "@phosphor-icons/react";
import { downloadFromBackend } from "../../lib/download";
import FuelCardForm from "../../components/FuelCardForm";
import AdminTransferCardModal from "../../components/AdminTransferCardModal";
import { formatCard } from "../../lib/cardDisplay";
import { Can } from "../../lib/rbac";

const emptyCard = { network: "esso", card_number: "", customer_id: "", status: "active", notes: "", unit: "", phone: "" };

export default function Cards() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [network, setNetwork] = useState("");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyCard);
  const [editing, setEditing] = useState(null);  // {card_id, ...}
  const [showImport, setShowImport] = useState(false);
  const [importNetwork, setImportNetwork] = useState("esso");
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/cards", { params: { network: network || undefined, q: q || undefined } });
    setRows(data);
  };
  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [network, q]);

  const save = async () => {
    try {
      const { data } = await api.post("/cards", form);
      const rm = data?.rematch?.rematched || 0;
      toast.success(rm > 0 ? `Card added · ${rm} existing transaction(s) rematched to customer` : "Card added");
      setShowAdd(false); setForm(emptyCard); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async () => {
    // PUT /cards/{id} expects the same CardIn schema as POST. We strip server-
    // side fields (`card_id`, `customer_name`, `created_at`, etc.) before
    // sending so Pydantic doesn't reject the payload.
    const allowed = ["network", "card_number", "customer_id", "truck_id", "driver_id", "status", "notes", "unit", "phone"];
    const body = {};
    for (const k of allowed) {
      if (editing[k] !== undefined && editing[k] !== null) body[k] = editing[k];
    }
    if (!body.customer_id) body.customer_id = null;
    try {
      const { data } = await api.put(`/cards/${editing.card_id}`, body);
      const rm = data?.rematch?.rematched || 0;
      toast.success(rm > 0 ? `Card updated · ${rm} existing transaction(s) rematched` : "Card updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };
  const [deleting, setDeleting] = useState(null); // {card_id, ...preview}
  const [ackChecked, setAckChecked] = useState(false);

  const del = async (id) => {
    // Phase 3 (Feb 2026) — never issue a blind hard delete. Fetch the
    // 4-class relationship preview first, then show a class-specific
    // confirmation with an explicit acknowledgement checkbox where
    // required.
    try {
      const { data } = await api.delete(`/cards/${id}`, { params: { dry_run: true } });
      setAckChecked(false);
      setDeleting(data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || e.response?.data?.detail || "Delete preview failed");
    }
  };
  const confirmDelete = async () => {
    if (!deleting || !deleting.hard_delete_allowed) return;
    if (deleting.warning_ack_required && !ackChecked) return;
    try {
      await api.delete(`/cards/${deleting.card_id}`, {
        params: deleting.warning_ack_required
          ? { relationship_warning_acknowledged: true }
          : undefined,
      });
      toast.success("Card deleted");
      setDeleting(null); setAckChecked(false); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "CARD_DELETE_BLOCKED_DEPENDENCIES") {
        // Race: direct dependency appeared between preview and confirm.
        toast.error(`Deletion blocked — ${d.direct_dependency_count ?? d.dependency_count} direct transaction reference(s) appeared. Card was not deleted.`);
        setDeleting({
          ...deleting,
          relationship_class: "A_DIRECT_REFERENCE",
          direct_dependency_count: d.direct_dependency_count ?? d.dependency_count,
          hard_delete_allowed: false,
          warning_ack_required: false,
          recommended_action: "mark_inactive",
        });
        setAckChecked(false);
      } else if (d?.code === "CARD_DELETE_RELATIONSHIP_ACK_REQUIRED") {
        // Race: classification changed to B/C between preview and confirm.
        toast.error(`Deletion blocked — this card now requires explicit acknowledgement (${d.relationship_class}). Card was not deleted.`);
        setDeleting({
          ...deleting,
          relationship_class: d.relationship_class,
          direct_dependency_count: d.direct_dependency_count,
          deterministic_legacy_count: d.deterministic_legacy_count,
          ambiguous_relationship_count: d.ambiguous_relationship_count,
          ambiguous_candidate_count: d.ambiguous_candidate_count,
          future_match_semantics_change: d.future_match_semantics_change,
          hard_delete_allowed: true,
          warning_ack_required: true,
          recommended_action: "review_then_delete",
        });
        setAckChecked(false);
      } else {
        toast.error(d?.message || d || "Delete failed");
        setDeleting(null);
        setAckChecked(false);
      }
    }
  };

  const downloadCsv = async () => {
    const params = {};
    if (network) params.network = network;
    if (q) params.q = q;
    try {
      const filename = await downloadFromBackend("/cards/export.csv", params);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  const [rematching, setRematching] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const rematchAll = async () => {
    if (!window.confirm(
      "Re-scan every active card and pull any unmatched transactions into the right customer?\n\n" +
      "This is safe — already-invoiced transactions are never touched.",
    )) return;
    setRematching(true);
    try {
      const { data } = await api.post("/cards/rematch-all");
      const msg = `Rematched ${data.transactions_rematched} transaction(s) across ${data.cards_with_matches} card(s) · ${data.repriced_to_pending} now PENDING` + (data.still_flagged ? ` · ${data.still_flagged} still flagged` : "");
      if (data.transactions_rematched > 0) toast.success(msg);
      else toast.info("Nothing to rematch — every card's transactions are already linked.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rematch failed");
    } finally {
      setRematching(false);
    }
  };

  return (
    <SectionPad>
      <PageHeader
        title="Fuel cards"
        subtitle="REGISTRY / CARDS"
        testId="cards-header"
        actions={
          <>
            <Can perm="cards.edit">
            <button className="gf-btn gf-btn-outline" onClick={rematchAll} disabled={rematching} data-testid="rematch-all-cards-button" title="Scan every card and pull unmatched transactions into the right customer">
              <ArrowsClockwise size={14}/> {rematching ? "Rematching…" : "Rematch unmatched"}
            </button>
            <button className="gf-btn gf-btn-outline" onClick={() => setShowTransfer(true)} data-testid="transfer-card-button" title="Transfer a card to another customer or unit (effective-dated)">
              <ArrowsLeftRight size={14}/> Transfer Card
            </button>
            </Can>
            <button className="gf-btn gf-btn-outline" onClick={downloadCsv} data-testid="download-cards-csv-button" title="Download the fuel card list (CSV)">
              <DownloadSimple size={14}/> Download CSV
            </button>
            <Can perm="cards.add">
            <button className="gf-btn gf-btn-outline" onClick={() => { setShowImport(true); setPreview(null); setSelected(new Set()); }} data-testid="import-cards-button">
              <UploadSimple size={14}/> Import cards
            </button>
            <button className="gf-btn gf-btn-primary" onClick={() => setShowAdd(true)} data-testid="add-card-button">
              <Plus size={14}/> Add card
            </button>
            </Can>
          </>
        }
      />

      <div className="gf-card">
        <div className="gf-card-header">
          <div className="flex items-center gap-3">
            <select className="gf-input" value={network} onChange={(e) => setNetwork(e.target.value)} style={{ width: 160 }} data-testid="card-network-filter">
              <option value="">All networks</option>
              <option value="esso">Esso CAD/L</option>
              <option value="pipeline">Pipeline CAD/L</option>
              <option value="petro_pass">Petro-Pass</option>
              <option value="ta_petro">Esso USD/GAL</option>
            </select>
            <input className="gf-input" placeholder="Search card number…" value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 240 }} data-testid="card-search" />
          </div>
          <div className="overline">{rows.length} CARDS</div>
        </div>
        <div className="overflow-x-auto">
          <table className="gf-table">
            <thead>
              <tr><th>CARD</th><th>NETWORK</th><th>CUSTOMER</th><th>UNIT #</th><th>NOTES</th><th>STATUS</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.card_id}>
                  <td className="font-mono font-semibold" data-testid={`card-row-${r.card_id}`}>{formatCard(r.card_number, r.network, r.last4)}</td>
                  <td className="font-mono text-xs">{r.network.toUpperCase()}</td>
                  <td>{r.customer_name || <span className="text-zinc-400">— unassigned —</span>}</td>
                  <td className="font-mono text-xs">{r.unit || <span className="text-zinc-400">—</span>}</td>
                  <td className="text-xs">{r.notes}</td>
                  <td><Badge variant={r.status === "active" ? "success" : "muted"}>{r.status}</Badge></td>
                  <td className="text-right whitespace-nowrap">
                    <Can perm="cards.edit">
                    <button
                      className="gf-btn gf-btn-ghost"
                      onClick={() => setEditing({ ...r })}
                      data-testid={`edit-card-${r.card_id}`}
                      title="Edit / re-assign card"
                    >
                      <PencilSimple size={14}/>
                    </button>
                    </Can>
                    <Can perm="cards.delete">
                    <button className="gf-btn gf-btn-ghost" onClick={() => del(r.card_id)} data-testid={`delete-card-${r.card_id}`} title="Delete card"><Trash size={14}/></button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---- DELETE CONFIRM / BLOCK MODAL (Phase 3 · 4-class policy) ---- */}
      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setDeleting(null); setAckChecked(false); }} data-testid="delete-card-modal">
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Delete card · {deleting.masked_card}</h3></div>
            <div className="p-4 space-y-2 text-sm">
              <div><span className="text-zinc-500">Network:</span> {deleting.network}</div>
              <div><span className="text-zinc-500">Status:</span> {deleting.status || "—"}</div>
              <div><span className="text-zinc-500">Class:</span> <span data-testid="delete-card-class">{deleting.relationship_class}</span></div>
              <div><span className="text-zinc-500">Direct references:</span> <span data-testid="delete-card-direct-count">{deleting.direct_dependency_count ?? deleting.dependency_count ?? 0}</span></div>

              {deleting.relationship_class === "A_DIRECT_REFERENCE" && (
                <div className="mt-3 rounded border border-red-300 bg-red-50 p-3" data-testid="delete-card-class-a-msg">
                  This card has {deleting.direct_dependency_count ?? deleting.dependency_count ?? 0} direct historical transaction reference(s) and cannot be permanently deleted. Mark it inactive instead.
                </div>
              )}

              {deleting.relationship_class === "B_DETERMINISTIC_LEGACY" && (
                <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="delete-card-class-b-msg">
                  <div>
                    This card has no direct transaction references, but <b data-testid="delete-card-legacy-count">{deleting.deterministic_legacy_count}</b> historical transaction(s) are deterministically associated with this card under current matching rules. Permanently deleting the card will not change these historical transactions, but future inbound rows matching this card will no longer be auto-attributed. Recommended: mark inactive.
                  </div>
                  <label className="flex items-start gap-2 pt-1">
                    <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} data-testid="delete-card-legacy-ack" />
                    <span>I understand this card has {deleting.deterministic_legacy_count} historical transaction attribution(s).</span>
                  </label>
                </div>
              )}

              {deleting.relationship_class === "C_AMBIGUOUS_POSSIBLE" && (
                <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="delete-card-class-c-msg">
                  <div>
                    This card is part of an ambiguous historical/future match group. Deleting it may change how future transactions are attributed to a remaining card. No existing transaction values will be changed by this deletion.
                    {(deleting.ambiguous_relationship_count ?? 0) > 0 && (
                      <> Currently ambiguous transactions: <b data-testid="delete-card-ambiguous-count">{deleting.ambiguous_relationship_count}</b>.</>
                    )}
                  </div>
                  <label className="flex items-start gap-2 pt-1">
                    <input type="checkbox" checked={ackChecked} onChange={(e) => setAckChecked(e.target.checked)} data-testid="delete-card-ambiguous-ack" />
                    <span>I understand deleting this card may change future card matching.</span>
                  </label>
                </div>
              )}

              {deleting.relationship_class === "D_NO_RELATIONSHIP" && (
                <div className="mt-3 rounded border border-zinc-200 bg-zinc-50 p-3" data-testid="delete-card-class-d-msg">
                  This card has no direct transaction references and can be permanently deleted.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => { setDeleting(null); setAckChecked(false); }} data-testid="delete-card-cancel">Cancel</button>
              {deleting.hard_delete_allowed && (
                <button
                  className="gf-btn gf-btn-primary"
                  onClick={confirmDelete}
                  disabled={deleting.warning_ack_required && !ackChecked}
                  data-testid="delete-card-confirm"
                >Delete permanently</button>
              )}
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>New card</h3></div>
            <FuelCardForm
              value={form}
              onChange={(patch) => setForm({ ...form, ...patch })}
              isEdit={false}
              customers={customers}
              testIdPrefix="cards-add"
            />
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={save} data-testid="save-card-button">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- EDIT CARD MODAL ---- */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-md border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Edit card · {formatCard(editing.card_number, editing.network, editing.last4)}</h3></div>
            <FuelCardForm
              value={editing}
              onChange={(patch) => setEditing({ ...editing, ...patch })}
              isEdit={true}
              customers={customers}
              testIdPrefix="cards-edit"
              excludeCardId={editing.card_id}
            />
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={saveEdit} data-testid="save-edit-card-button">Save</button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !importing && setShowImport(false)}>
          <div className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[92vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>Import fuel cards</h3>
              <div className="overline">AUTO-MATCH TO CUSTOMERS</div>
            </div>
            <div className="gf-card-body overflow-y-auto flex-1">
              {!preview ? (
                <div className="space-y-3">
                  <div>
                    <div className="overline mb-1">Network</div>
                    <select className="gf-input" value={importNetwork} onChange={(e) => setImportNetwork(e.target.value)}>
                      <option value="esso">Esso (card lookup TSV/XLS)</option>
                      <option value="pipeline">Pipeline (card list XLS)</option>
                      <option value="petro_pass">Petro-Pass (card list XLSX)</option>
                    </select>
                  </div>
                  <div>
                    <div className="overline mb-1">File</div>
                    <input ref={fileRef} type="file" className="gf-input" accept=".csv,.xls,.xlsx,.tsv" data-testid="cards-import-file" />
                  </div>
                  <div className="text-xs text-zinc-500">
                    Esso: upload the card lookup with X-Ref column.<br />
                    Pipeline: upload the cards XLS with the "Notes" column containing the customer name.
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="overline">FOUND {preview.count} CARDS</div>
                      <div className="text-xs text-zinc-500 mt-1">{selected.size} selected · existing cards update, new cards create</div>
                    </div>
                    <div className="flex gap-2">
                      <button className="gf-btn gf-btn-outline" onClick={() => setSelected(new Set(preview.cards.map((_, i) => i)))} data-testid="select-all-cards">Select all</button>
                      <button className="gf-btn gf-btn-outline" onClick={() => setSelected(new Set(preview.cards.map((c, i) => c.matched_customer_id ? i : null).filter((x) => x !== null)))}>Only matched</button>
                      <button className="gf-btn gf-btn-outline" onClick={() => setSelected(new Set())}>Clear</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-zinc-200" style={{ maxHeight: 500 }}>
                    <table className="gf-table">
                      <thead><tr>
                        <th style={{ width: 36 }}></th>
                        <th>CARD</th><th>UNIT</th><th>HINT (provider X-Ref)</th><th>MATCHED CUSTOMER</th><th>STATUS</th>
                      </tr></thead>
                      <tbody>
                        {preview.cards.map((c, i) => (
                          <tr key={i} style={c.exists ? { background: "#FEF9C3" } : (!c.matched_customer_id ? { background: "#FEE2E2" } : {})}>
                            <td><input type="checkbox" checked={selected.has(i)} onChange={(e) => {
                              const n = new Set(selected);
                              if (e.target.checked) n.add(i); else n.delete(i);
                              setSelected(n);
                            }} /></td>
                            <td className="font-mono font-semibold">{formatCard(c.card_number, importNetwork, c.last4)}</td>
                            <td className="font-mono text-xs">{c.unit || "—"}</td>
                            <td className="text-xs">{c.customer_hint}</td>
                            <td>
                              <select className="gf-input" style={{ minWidth: 220 }}
                                value={c.matched_customer_id || ""}
                                onChange={(e) => {
                                  const cs = [...preview.cards];
                                  const cust = customers.find((x) => x.customer_id === e.target.value);
                                  cs[i] = { ...cs[i], matched_customer_id: e.target.value || null, matched_customer_name: cust?.company_name || "" };
                                  setPreview({ ...preview, cards: cs });
                                }}>
                                <option value="">— unassigned —</option>
                                {customers.map((cust) => (
                                  <option key={cust.customer_id} value={cust.customer_id}>{cust.company_name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              {c.exists ? <Badge variant="warning">UPDATE</Badge> : !c.matched_customer_id ? <Badge variant="error">UNMATCHED</Badge> : <Badge variant="success">NEW</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => { setShowImport(false); setPreview(null); }} disabled={importing}>Close</button>
              {!preview ? (
                <button className="gf-btn gf-btn-primary" disabled={importing}
                  data-testid="preview-cards-button"
                  onClick={async () => {
                    const f = fileRef.current?.files?.[0];
                    if (!f) return toast.error("Choose a file");
                    setImporting(true);
                    try {
                      const fd = new FormData();
                      fd.append("network", importNetwork);
                      fd.append("file", f);
                      const { data } = await api.post("/cards/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                      setPreview(data);
                      setSelected(new Set(data.cards.map((c, i) => c.matched_customer_id ? i : null).filter((x) => x !== null)));
                      toast.success(`Parsed ${data.count} cards, ${data.cards.filter((c) => c.matched_customer_id).length} auto-matched`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Parse failed"); }
                    finally { setImporting(false); }
                  }}>
                  {importing ? "Parsing…" : "Preview"}
                </button>
              ) : (
                <button className="gf-btn gf-btn-primary" disabled={importing || selected.size === 0}
                  data-testid="import-cards-confirm"
                  onClick={async () => {
                    setImporting(true);
                    try {
                      const picked = [...selected].map((i) => preview.cards[i]);
                      const { data } = await api.post("/cards/bulk-create", { cards: picked, network: importNetwork });
                      toast.success(`${data.created} created · ${data.updated} updated · ${data.unassigned} unassigned`);
                      setShowImport(false); setPreview(null); setSelected(new Set());
                      load();
                    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
                    finally { setImporting(false); }
                  }}>
                  {importing ? "Importing…" : `Import ${selected.size} card(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <AdminTransferCardModal
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        onSubmitted={() => { load(); }}
      />
    </SectionPad>
  );
}
